import {Option} from '@/cart/types';
import {isRecurringPlan} from '@/product/create-delivery-options';
import {isSizeOption, PACK_SIZE} from '@/product/product-selection/pack-size';
import {
  ProductSelectionState,
  ProductState,
  VariantState,
} from '@/product/types';
import {getProductSellingPlans} from '@/selling-plan/selling-plan-store';
import {effect, signal} from '@preact/signals';
import {Plan} from '@rechargeapps/storefront-client';

const productSelection = signal<ProductSelectionState>({
  product: null,
  selectedVariant: null,
  plans: [],
  selectedPlan: null,
  monthlyPlans: [],
  onetimePlan: null,
  packOptions: [],
});

let mapVariants = (variant: VariantState) => variant;

effect(() => {
  onUpdateState(productSelection.value);
});

/**
 * Listener for the product selection state updates.
 * Loads the selling plans for the product if they are not already loaded.
 * Add other listeners here if needed.
 *
 * @param state - The current state of the product selection.
 */
function onUpdateState(state: ProductSelectionState) {
  const shouldLoadPlans = state.product && state.plans.length === 0;
  if (!shouldLoadPlans) return;

  const plans = getProductSellingPlans(state.product!.id);
  if (plans.length > 0) {
    productSelection.value = setPlans(state, plans);
  }
}

export function setMapVariants(fn: (variant: VariantState) => VariantState) {
  mapVariants = fn;
}

/**
 * Retrieves the current product selection state.
 * @returns The product selection state.
 */
export function getProductSelection(): ProductSelectionState {
  const {product, ...rest} = productSelection.value;

  return {
    product: product
      ? {
          ...product,
          variants: product.variants.map(mapVariants),
        }
      : product,
    ...rest,
  };
}

/**
 * Sets the selected product.
 *
 * @param product - The product state to set.
 */
export function setProduct(product: ProductState) {
  const state = productSelection.value;
  productSelection.value = handleSetProduct(state, product);
}

/**
 * Selects a plan for the product.
 * @param plan - The plan to be selected.
 * @throws If the product selection state is not initialized.
 */
export function selectPlan(plan: Plan | null) {
  const state = getProductSelection();
  if (!state) {
    throw new Error(
      'Failed to select plan. Product selection state is not initialized.'
    );
  }
  productSelection.value = {
    ...state,
    selectedPlan: plan,
  };
}

/**
 * Selects an option for the product.
 *
 * @param option - The option to be selected.
 * @throws Error if the product selection state is not initialized.
 */
export function selectOption(option: Option) {
  const state = getProductSelection();
  if (!state) {
    throw new Error(
      'Failed to select option. Product selection state is not initialized.'
    );
  }
  productSelection.value = handleSelectOption(option, state);
}

/**
 * Handles the product selection by setting the selected variant and pack options.
 * @param state - The current state of the product selection.
 * @param product - The selected product state.
 * @returns The updated state with the selected variant and pack options.
 */
function handleSetProduct(state: ProductSelectionState, product: ProductState) {
  const selectedVariant = product.variants[0];

  const packOptions = Array.from(
    product.variants.reduce((set, variant) => {
      const packSize = variant.selectedOptions.find((option) =>
        isSizeOption(option, product.sizeOptionNames)
      )?.value;
      if (packSize) {
        set.add(packSize);
      }
      return set;
    }, new Set<string>())
  );

  packOptions.sort((a, b) => parseFloat(a) - parseFloat(b));

  return {
    ...state,
    product,
    selectedVariant,
    packOptions,
  };
}

/**
 * Handles the selection of an option for a product.
 *
 * @param option - The option to be selected.
 * @param state - The current state of the product selection.
 * @returns The updated product selection state after selecting the option.
 * @throws Error if no product is found in the state.
 */
function handleSelectOption(
  option: Option,
  state: ProductSelectionState
): ProductSelectionState {
  const {selectedVariant, product, ...rest} = state;
  if (!product) {
    throw new Error('Failed to select option. No product found.');
  }

  const {selectedOptions} =
    selectedVariant ??
    product?.selectedOrFirstAvailableVariant ??
    product.variants[0];

  const newOptions = selectedOptions.map(({name, value}) => {
    if (name !== option.name) {
      return {name, value};
    }

    return {
      name: name,
      value: option.value,
    };
  });

  const newVariant = product.variants.find(({selectedOptions}) =>
    selectedOptions.every((option) =>
      newOptions.some(
        ({name, value}) => name === option.name && value === option.value
      )
    )
  );

  return {
    selectedVariant: newVariant ?? selectedVariant,
    product,
    ...rest,
  };
}

/**
 * Sets the plans for the product selection state.
 *
 * @param state - The current product selection state.
 * @param plans - An array of plans to set.
 * @returns The updated product selection state with the new plans.
 */
function setPlans(state: ProductSelectionState, plans: Plan[]) {
  const onetimePlan = getDefaultOnetimePlan(plans);

  const isMonthlyPlan = (plan: Plan) =>
    isRecurringPlan(plan) &&
    plan.subscription_preferences.order_interval_frequency &&
    plan.subscription_preferences.order_interval_frequency < 12 &&
    plan.subscription_preferences.interval_unit === 'month';

  const monthlyPlans = plans
    .filter(isMonthlyPlan)
    .sort(
      (a, b) =>
        a.subscription_preferences.order_interval_frequency! -
        b.subscription_preferences.order_interval_frequency!
    );

  const selectedPlan = monthlyPlans[0] ?? onetimePlan;

  return {
    ...state,
    plans,
    monthlyPlans,
    onetimePlan,
    selectedPlan,
  };
}

/**
 * Returns the default one-time plan from the given array of plans.
 * The default one-time plan is defined as a plan that:
 * - Has an external plan ID
 * - Expires after a specific number of charges (1 charge)
 * - Has a discount amount of 0
 *
 * @param plans - The array of plans to search from.
 * @returns The default one-time plan, or null if no such plan is found.
 */
export function getDefaultOnetimePlan(plans: Plan[]): Plan | null {
  return (
    plans.find(
      (plan) =>
        !!plan.external_plan_id &&
        plan.subscription_preferences
          .expire_after_specific_number_of_charges === 1 &&
        plan.discount_amount != null &&
        parseFloat(plan.discount_amount) === 0
    ) ?? null
  );
}

/**
 * Retrieves the pack variant based on the selected pack option.
 * This is a utility function to preview the selected variant of a pack option.
 * @param packOption - The selected pack option.
 * @returns The pack variant if found, otherwise the selected variant.
 */
export function getPackVariant(packOption: string) {
  const {selectedVariant, product} = getProductSelection();
  if (!selectedVariant || !product) return null;

  const options = new Map(
    selectedVariant.selectedOptions.map(({name, value}) => [name, value])
  );
  const packOptionName =
    product.options.find((option) =>
      isSizeOption(option, product.sizeOptionNames)
    )?.name ?? PACK_SIZE;
  options.set(packOptionName, packOption);
  const packVariant = product.variants.find((variant) =>
    variant.selectedOptions.every(
      ({name, value}) => options.get(name) === value
    )
  );

  return packVariant ?? selectedVariant;
}
